'use strict'
/* eslint-disable no-unused-vars */
// eslint-disable-next-line no-unused-vars

/** Fonts */
import fontLatin from './fonts/fontLatin.js';
import fontCh from './fonts/fontCht.js';
import fontJpn from './fonts/fontJpn.js';
import fontKor from './fonts/fontKor.js';
import text1 from './fonts/font1.js';
import text2 from './fonts/font2.js';
import text3 from './fonts/font3.js';

/** Resources */
import pokedex from './resources/Showdown/pokedex.js';
import natures from './resources/Showdown/natures.js';
import PokeTranslator from './resources/Pokes/TranslatorPokes.js';
import AbilityTranslator from './resources/Abilities/TranslatorAbilities.js';
import TypeTranslator from './resources/Types/TranslatorTypes.js';
import ItemTranslator from './resources/Items/TranslatorItems.js';
import MoveTranslator from './resources/Moves/TranslatorMoves.js';
import NaturesTranslator from './resources/Natures/TranslatorNatures.js';

import { jsPDF } from 'jspdf';
import { Koffing } from './koff.mjs';

import { generateAllLanguageOTS } from './generateAllLanguageOTS.js';

/**
 * NATURALEZAS
 * Reconoce nombres en español e inglés.
 * Se utiliza la clave inglesa de Pokémon Showdown.
 */
const NATURE_EFFECTS = {
  Adamant: ['atk', 'spa'],
  Bold: ['def', 'atk'],
  Brave: ['atk', 'spe'],
  Calm: ['spd', 'atk'],
  Careful: ['spd', 'spa'],
  Gentle: ['spd', 'def'],
  Hasty: ['spe', 'def'],
  Impish: ['def', 'spa'],
  Jolly: ['spe', 'spa'],
  Lax: ['def', 'spd'],
  Lonely: ['atk', 'def'],
  Mild: ['spa', 'def'],
  Modest: ['spa', 'atk'],
  Naive: ['spe', 'spd'],
  Naughty: ['atk', 'spd'],
  Quiet: ['spa', 'spe'],
  Rash: ['spa', 'spd'],
  Relaxed: ['def', 'spe'],
  Sassy: ['spd', 'spe'],
  Timid: ['spe', 'atk']
};

const STAT_LABELS = {
  atk: 'Atk',
  def: 'Def',
  spa: 'SpA',
  spd: 'SpD',
  spe: 'Spe'
};

const NATURE_ES_TO_EN = {
  fuerte: 'Hardy',
  hurana: 'Lonely',
  audaz: 'Brave',
  firme: 'Adamant',
  picara: 'Naughty',
  osada: 'Bold',
  docil: 'Docile',
  placida: 'Relaxed',
  agitada: 'Impish',
  floja: 'Lax',
  miedosa: 'Timid',
  activa: 'Hasty',
  seria: 'Serious',
  alegre: 'Jolly',
  ingenua: 'Naive',
  modesta: 'Modest',
  afable: 'Mild',
  mansa: 'Quiet',
  timida: 'Bashful',
  alocada: 'Rash',
  serena: 'Calm',
  amable: 'Gentle',
  grosera: 'Sassy',
  cauta: 'Careful',
  rara: 'Quirky'
};

const NEUTRAL_NATURES = new Set([
  'Bashful',
  'Docile',
  'Hardy',
  'Quirky',
  'Serious'
]);

function normalizeNatureName(value) {
  return String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function resolveNatureName(value) {
  const normalized = normalizeNatureName(value);

  if (!normalized) return null;

  const english = [
    ...Object.keys(NATURE_EFFECTS),
    ...NEUTRAL_NATURES
  ].find(name => normalizeNatureName(name) === normalized);

  return english || NATURE_ES_TO_EN[normalized] || null;
}

function getNatureEffect(natureName) {
  const key = resolveNatureName(natureName);

  if (!key) return '';

  if (NEUTRAL_NATURES.has(key)) {
    return 'Neutral';
  }

  const [up, down] = NATURE_EFFECTS[key];

  return `+${STAT_LABELS[up]} / -${STAT_LABELS[down]}`;
}

/**
 * Firma discreta en el pie del PDF.
 */
function addPokeCircuitFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(125, 125, 125);

  doc.text(
    'Generated by pokecircuit.com',
    pageWidth - 7,
    pageHeight - 5,
    { align: 'right' }
  );

  doc.setTextColor(0, 0, 0);
}

/**
 * GENERADOR PRINCIPAL DE PDF
 */
export async function generateTeamPDF(data) {
  const {
    playerName,
    trainerName,
    teamName,
    switchName,
    playerId,
    birth,
    supportId = '',
    ageDivision,
    sheet,
    lang,
    paste,
    onlyPdf,
    inputMode
  } = data;

  const fontMap = {
    Cht: fontCh,
    Chs: fontCh,
    Jpn: fontJpn,
    Kor: fontKor
  };

  if (onlyPdf === true) {
    if (!sheet || !lang) {
      throw new Error('Missing required data');
    }
  } else {
    if (!sheet || !paste || !lang) {
      throw new Error('Missing required data');
    }
  }

  let msg, x, y, mygap;

  if (
    data?.sheet === 'allLanguage' ||
    data?.lang === 'allLanguage' ||
    data?.allLanguage === true
  ) {
    return generateAllLanguageOTS(data);
  }

  const parsedTeam = Koffing.parse(paste);

  const doc = new jsPDF();

  loadBaseFonts(doc);

  let chosenLang = '';

  let pokes = parsedTeam.teams[0].pokemon;

  if (onlyPdf === true) {
    pokes = [];
  }

  let ivs = {
    hp: 31,
    atk: 31,
    def: 31,
    spa: 31,
    spd: 31,
    spe: 31
  };

  let evs = {
    hp: 0,
    atk: 0,
    def: 0,
    spa: 0,
    spd: 0,
    spe: 0
  };

  let level = 100;
  let nature = 'Serious';

  let statX = 100;
  let pokeY = 67;
  let levelY = pokeY + 9.5;
  let gapx = 21;

  if (sheet == 'open' || sheet == 'staff') {
    chosenLang = lang;

    const selectedFont = fontMap[lang] || fontLatin;

    doc.addFileToVFS('customFont.ttf', selectedFont);
    doc.addFont('customFont.ttf', 'customFont', 'normal');
    doc.setFont('customFont', 'normal');

    doc.setFontSize(7);
    doc.setFont('text2', 'normal');

    msg = 'All Pokémon must be listed exactly as they appear in the Battle Team,';
    doc.text(50, 272, msg);

    doc.setFont('text1', 'normal');

    msg = 'at the level they are in the game.';
    doc.text(120.5, 272, msg);

    doc.setFontSize(13);
    doc.setFont('text1', 'normal');

    msg = 'Pokémon Video Game Team List';
    doc.text(73, 12.5, msg);

    doc.setLineWidth(0.3);

    x = 45;
    y = 34.5;
    mygap = 7;

    for (let i = 0; i < 4; i++) {
      doc.line(
        x,
        y + mygap * i,
        x + 65,
        y + mygap * i
      );
    }

    doc.setFontSize(12);
    doc.setFont('text1', 'normal');

    msg = 'Player Name: ';
    doc.text(45, 33, msg, 'right');

    doc.setFontSize(9);

    msg = 'Trainer Name in Game: ';
    doc.text(45, 40, msg, 'right');

    msg = 'Battle Team Number / Name: ';
    doc.text(45, 47, msg, 'right');

    msg = 'Switch Profile Name: ';
    doc.text(45, 54, msg, 'right');

    x = 155;
    gapx = 21;

    for (let i = 0; i < 3; i++) {
      doc.rect(x + gapx * i, 30, 4, 4);
    }

    msg = 'Age Division: ';
    doc.text(140, 33, msg, 'right');

    msg = 'Juniors ';
    doc.text(154, 33, msg, 'right');

    msg = 'Seniors ';
    doc.text(175, 33, msg, 'right');

    msg = 'Masters ';
    doc.text(196, 33, msg, 'right');

    doc.setFont('text2', 'normal');
    doc.setFontSize(13);

    doc.text(playerName, 47, 33);
    doc.text(trainerName, 47, 40);
    doc.text(teamName, 47, 47);
    doc.text(switchName, 47, 54);

    /**
     * RECUADROS DE LOS 6 POKÉMON
     */
    for (let i = 0; i < 6; i++) {
      doc.setLineWidth(0.6);

      x = 6.5 + 99 * (i % 2);
      y = 59.5 + 70 * Math.floor(i / 2);

      doc.rect(x, y, 95, 68);

      doc.setLineWidth(0.4);

      let startY = 12;
      mygap = 8;

      for (let b = 0; b < 7; b++) {
        doc.line(
          x,
          y + startY + mygap * b,
          x + 95,
          y + startY + mygap * b
        );
      }
    }

    if (ageDivision !== undefined && ageDivision !== null) {
      doc.setLineWidth(1);

      let posX = 154 + 21 * ageDivision;

      doc.line(posX, 29, posX + 6, 35);
      doc.line(posX + 6, 29, posX, 35);
    }

    const t = await loadTranslations(lang);

    /**
     * FORMATO VACÍO
     */
    if (onlyPdf === true) {
      for (let i = 0; i < 6; i++) {
        let textX = 35;
        statX = 100;

        let gapX = 100;
        let textXX = 27.5;

        pokeY = 67;

        let teraY = pokeY + 9.5;
        levelY = pokeY + 9.5;

        let abilityY = pokeY + 18;
        let itemY = pokeY + 26;
        let gapY = 70;

        let moveY = pokeY + 34;
        let moveGapY = 8;

        let itemId = 'NOITEM';
        let item = 'NO ITEM';

        if (itemId != 'NOITEM') {
          item = t.items[itemId];
        }

        let movs = [];

        // Nombre
        doc.setFontSize(13);
        doc.setFont('text1', 'normal');

        doc.text(
          'Pokémon',
          textXX + (i % 2) * gapX,
          pokeY + Math.floor(i / 2) * gapY,
          'right'
        );

        // Stat Alignment
        doc.setFontSize(11);
        doc.setFont('text1', 'normal');

        doc.text(
          'Stat Alignment',
          (textXX + 5) + (i % 2) * gapX,
          teraY + Math.floor(i / 2) * gapY,
          'right'
        );

        // Ability
        doc.setFontSize(13);
        doc.setFont('text1', 'normal');

        doc.text(
          'Ability',
          textXX + (i % 2) * gapX,
          abilityY + Math.floor(i / 2) * gapY,
          'right'
        );

        // Item
        doc.setFontSize(13);
        doc.setFont('text1', 'normal');

        doc.text(
          'Held Item',
          textXX + (i % 2) * gapX,
          itemY + Math.floor(i / 2) * gapY,
          'right'
        );

        for (let j = 0; j < 4; j++) {
          doc.setFontSize(13);
          doc.setFont('text1', 'normal');

          doc.text(
            'Move ' + (j + 1),
            textXX + (i % 2) * gapX,
            moveY + Math.floor(i / 2) * gapY + j * moveGapY,
            'right'
          );
        }
      }
    }

    /**
     * INFORMACIÓN DE CADA POKÉMON
     */
    for (let i = 0; i < pokes.length; i++) {
      let textX = 35;
      statX = 100;

      let gapX = 100;
      let textXX = 27.5;

      pokeY = 67;

      let teraY = pokeY + 9.5;
      levelY = pokeY + 9.5;

      let abilityY = pokeY + 18;
      let itemY = pokeY + 26;
      let gapY = 70;

      let moveY = pokeY + 34;
      let moveGapY = 8;

      let nameId = PokeTranslator[pokes[i].name];
      let abilityId = AbilityTranslator[pokes[i].ability];

      /**
       * NATURALEZA:
       * Soporta nombre en español o inglés.
       */
      const originalNature = pokes[i].nature || 'Serious';
      const englishNature = resolveNatureName(originalNature);

      let natureId =
        NaturesTranslator[originalNature] ||
        NaturesTranslator[englishNature];

      let itemId = 'NOITEM';

      if (pokes[i].item) {
        itemId = ItemTranslator[pokes[i].item];
      }

      nature =
        (natureId && t.natures[natureId]) ||
        originalNature;

      if (pokes[i].level) {
        level = pokes[i].level;
      }

      if (pokes[i].ivs) {
        for (const [key, value] of Object.entries(pokes[i].ivs)) {
          ivs[key] = value;
        }
      }

      if (pokes[i].evs) {
        for (const [key, value] of Object.entries(pokes[i].evs)) {
          evs[key] = value;
        }
      }

      if (inputMode === 'paste') {
        if (!pokedex[pokes[i].name]) {
          throw new Error('ERROR IN PASTE');
        }
      }

      let name = t.pokes[nameId];

      if (name === undefined) {
        name = pokes[i].name;
      }

      let ability = t.abilities[abilityId];

      if (ability === undefined) {
        ability = pokes[i].ability;
      }

      let item = 'NO ITEM';

      if (itemId != 'NOITEM') {
        item = t.items[itemId];
      }

      if (item === undefined) {
        item = pokes[i].item;
      }

      let movs = [];

      for (
        let moveIndex = 0;
        moveIndex < pokes[i].moves.length;
        moveIndex++
      ) {
        let moveId = MoveTranslator[pokes[i].moves[moveIndex]];

        if (moveId === undefined) {
          movs.push(pokes[i].moves[moveIndex]);
        } else {
          movs.push(t.moves[moveId]);
        }
      }

      /**
       * NOMBRE DEL POKÉMON
       */
      doc.setFontSize(13);
      doc.setFont('text1', 'normal');

      doc.text(
        'Pokémon',
        textXX + (i % 2) * gapX,
        pokeY + Math.floor(i / 2) * gapY,
        'right'
      );

      doc.setFontSize(12);
      doc.setFont('customFont', 'normal');

      doc.text(
        name,
        textX + (i % 2) * gapX,
        pokeY + Math.floor(i / 2) * gapY
      );

      /**
       * STAT ALIGNMENT
       * Ejemplos:
       * Miedosa (+Spe / -Atk)
       * Timid (+Spe / -Atk)
       * Modesta (+SpA / -Atk)
       * Modest (+SpA / -Atk)
       */
      doc.setFontSize(11);
      doc.setFont('text1', 'normal');

      doc.text(
        'Stat Alignment',
        (textXX + 5) + (i % 2) * gapX,
        teraY + Math.floor(i / 2) * gapY,
        'right'
      );

      const natureEffect = getNatureEffect(originalNature);

      const natureLabel = natureEffect
        ? `${nature} (${natureEffect})`
        : nature;

      const natureMaxWidth = 66;

      doc.setFont('customFont', 'normal');

      let natureFontSize = 11;
      doc.setFontSize(natureFontSize);

      while (
        doc.getTextWidth(natureLabel) > natureMaxWidth &&
        natureFontSize > 7
      ) {
        natureFontSize -= 0.5;
        doc.setFontSize(natureFontSize);
      }

      doc.text(
        natureLabel,
        textX + (i % 2) * gapX,
        teraY + Math.floor(i / 2) * gapY,
        { maxWidth: natureMaxWidth }
      );

      /**
       * ABILITY
       */
      doc.setFontSize(13);
      doc.setFont('text1', 'normal');

      doc.text(
        'Ability',
        textXX + (i % 2) * gapX,
        abilityY + Math.floor(i / 2) * gapY,
        'right'
      );

      doc.setFontSize(11);
      doc.setFont('customFont', 'normal');

      doc.text(
        ability,
        textX + (i % 2) * gapX,
        abilityY + Math.floor(i / 2) * gapY
      );

      /**
       * HELD ITEM
       */
      doc.setFontSize(13);
      doc.setFont('text1', 'normal');

      doc.text(
        'Held Item',
        textXX + (i % 2) * gapX,
        itemY + Math.floor(i / 2) * gapY,
        'right'
      );

      doc.setFontSize(11);
      doc.setFont('customFont', 'normal');

      doc.text(
        item,
        textX + (i % 2) * gapX,
        itemY + Math.floor(i / 2) * gapY
      );

      /**
       * MOVIMIENTOS
       */
      for (let j = 0; j < movs.length; j++) {
        doc.setFontSize(13);
        doc.setFont('text1', 'normal');

        doc.text(
          'Move ' + (j + 1),
          textXX + (i % 2) * gapX,
          moveY + Math.floor(i / 2) * gapY + j * moveGapY,
          'right'
        );

        doc.setFontSize(11);
        doc.setFont('customFont', 'normal');

        doc.text(
          movs[j],
          textX + (i % 2) * gapX,
          moveY + Math.floor(i / 2) * gapY + j * moveGapY
        );
      }
    }
  }

  /**
   * OPEN TEAM SHEET
   */
  if (sheet === 'open') {
    doc.setFontSize(13);
    doc.setFont('text1', 'normal');

    msg = '2 of 2: ';
    doc.text(83, 18, msg);

    doc.setFont('text3', 'normal');

    msg = 'For Opponents';
    doc.text(96, 18, msg);

    doc.setFontSize(10);
    doc.setFont('text3', 'normal');

    msg = 'Do not lose this page! Keep it throughout the tournament, sharing it with your opponent each round.';
    doc.text(31, 24, msg);

    // Marca discreta al pie del documento
    addPokeCircuitFooter(doc);

    doc.save(
      playerId + '-' + playerName + '-openTeamSheet.pdf'
    );
  }

  /**
   * STAFF / CLOSED TEAM SHEET
   */
  if (sheet == 'staff') {
    doc.setFontSize(13);
    doc.setFont('text1', 'normal');

    msg = '1 of 2: ';
    doc.text(77, 18, msg);

    doc.setFont('text3', 'normal');

    msg = 'For Tournament Staff';
    doc.text(90, 18, msg);

    doc.setFontSize(10);
    doc.setFont('text3', 'normal');

    msg = 'Complete both pages of this document. Submit this page to event staff before the tournament, at the time set by the Organizer.';
    doc.text(12, 24, msg);

    doc.setLineWidth(0.3);

    /**
     * CAMPOS SUPERIORES DERECHOS
     *
     * Player ID
     * Date of Birth
     * Support ID
     */

    // Player ID
    doc.setFontSize(9);
    doc.setFont('text1', 'normal');

    msg = 'Player ID: ';
    doc.text(140, 40, msg, 'right');

    doc.line(140, 41.5, 180, 41.5);

    doc.setFontSize(13);
    doc.setFont('text2', 'normal');

    doc.text(
      String(playerId ?? ''),
      142,
      40
    );

    // Date of Birth
    doc.setFontSize(9);
    doc.setFont('text1', 'normal');

    msg = 'Year of Birth: ';
    doc.text(140, 47, msg, 'right');

    doc.line(140, 48.5, 180, 48.5);

    doc.setFontSize(13);
    doc.setFont('text2', 'normal');

    const birthYear = birth
      ? String(birth).substring(0, 4)
      : '';

    doc.text(
      String(birthYear ?? ''),
      142,
      47
    );

    // Support ID
    doc.setFontSize(9);
    doc.setFont('text1', 'normal');

    msg = 'Support ID: ';
    doc.text(140, 54, msg, 'right');

    doc.line(140, 55.5, 180, 55.5);

    doc.setFontSize(13);
    doc.setFont('text2', 'normal');

    doc.text(
      String(supportId ?? ''),
      142,
      54
    );

    /**
     * COLUMNAS DE ESTADÍSTICAS
     */
    for (let i = 0; i < 6; i++) {
      doc.setLineWidth(0.4);

      x = 6.5 + 99 * (i % 2);
      y = 59.5 + 70 * Math.floor(i / 2);

      doc.line(
        x + 80,
        y + 12,
        x + 80,
        y + 68
      );

      doc.setFontSize(6);
      doc.setFont('text1', 'normal');

      doc.text(x + 81, y + 14, 'Level');
      doc.text(x + 81, y + 22, 'HP');
      doc.text(x + 81, y + 30, 'Atk');
      doc.text(x + 81, y + 38, 'Def');
      doc.text(x + 81, y + 46, 'Sp. Atk');
      doc.text(x + 81, y + 54, 'Sp. Def');
      doc.text(x + 81, y + 62, 'Speed');
    }

    doc.setFontSize(11);
    doc.setFont('customFont', 'normal');

    /**
     * ESTADÍSTICAS DE CADA POKÉMON
     */
    for (let i = 0; i < pokes.length; i++) {
      let stats = getStats(
        pokes[i].name,
        pokes[i].ivs,
        pokes[i].evs,
        pokes[i].level,
        resolveNatureName(pokes[i].nature) || pokes[i].nature,
        inputMode
      );

      let gapX = 100;
      let gapY = 70;

      let statY = pokeY + 19;
      let statGapY = 8;

      let pokemonLevel = pokes[i].level
        ? pokes[i].level
        : 100;

      doc.text(
        pokemonLevel.toString(),
        statX + (i % 2) * (gapX - 1),
        levelY + Math.floor(i / 2) * gapY,
        'right'
      );

      let j = 0;

      for (const [key, value] of Object.entries(stats)) {
        doc.text(
          value.toString(),
          statX + (i % 2) * (gapX - 1),
          statY + Math.floor(i / 2) * gapY + j * statGapY,
          'right'
        );

        j = j + 1;
      }
    }

    // Marca discreta al pie del documento
    addPokeCircuitFooter(doc);

    doc.save(
      playerId + '-' + playerName + '-closedTeamSheet.pdf'
    );
  }
}

/**
 * CÁLCULO DE ESTADÍSTICAS
 */
function getStats(
  poke,
  ivs = {},
  evs = {},
  level = 100,
  nat,
  inputMode
) {
  // MANUAL: tomar valores tal cual llegan
  if (inputMode === 'manual') {
    return {
      hp: evs.hp ?? 0,
      atk: evs.atk ?? 0,
      def: evs.def ?? 0,
      spa: evs.spa ?? 0,
      spd: evs.spd ?? 0,
      spe: evs.spe ?? 0
    };
  }

  // PASTE: mantener cálculo actual
  ivs = validateIVs(normalizeIVs(ivs));
  evs = validateEVs(normalizeEVs(evs));

  const ret = {
    hp: 0,
    atk: 0,
    def: 0,
    spa: 0,
    spd: 0,
    spe: 0
  };

  const baseStats = pokedex[poke];
  const nature = natures[nat];

  for (const [key] of Object.entries(baseStats)) {
    const effectiveEV = evs[key] * 8;

    if (key === 'hp') {
      ret.hp = Math.floor(
        (
          (
            (
              2 * baseStats.hp +
              ivs.hp +
              effectiveEV / 4
            ) * level
          ) / 100
        ) + level + 10
      );
    } else {
      ret[key] = Math.floor(
        Math.floor(
          (
            (
              (
                2 * baseStats[key] +
                ivs[key] +
                effectiveEV / 4
              ) * level
            ) / 100
          ) + 5
        ) * nature[key]
      );
    }
  }

  return ret;
}

/**
 * NORMALIZAR EVS
 */
function normalizeEVs(evs = {}) {
  const defaultEVs = {
    hp: 0,
    atk: 0,
    def: 0,
    spa: 0,
    spd: 0,
    spe: 0
  };

  return {
    ...defaultEVs,
    ...evs
  };
}

/**
 * NORMALIZAR IVS
 */
function normalizeIVs(ivs = {}) {
  const defaultIVs = {
    hp: 31,
    atk: 31,
    def: 31,
    spa: 31,
    spd: 31,
    spe: 31
  };

  return {
    ...defaultIVs,
    ...ivs
  };
}

/**
 * VALIDAR IVS
 */
function validateIVs(ivs) {
  for (const key in ivs) {
    if (ivs[key] > 31) {
      ivs[key] = 31;
    }

    if (ivs[key] < 0) {
      ivs[key] = 0;
    }
  }

  return ivs;
}

/**
 * VALIDAR EVS
 */
function validateEVs(evs) {
  let total = 0;

  for (const key in evs) {
    if (evs[key] > 32) {
      evs[key] = 32;
    }

    if (evs[key] < 0) {
      evs[key] = 0;
    }

    total += evs[key];
  }

  if (total > 66) {
    console.warn('Total EVs excede 66');
  }

  return evs;
}

/**
 * CARGAR TRADUCCIONES
 */
async function loadTranslations(lang) {
  const [
    types,
    abilities,
    items,
    moves,
    pokes,
    natures
  ] = await Promise.all([
    import(`./resources/Types/Types${lang}.js`),
    import(`./resources/Abilities/Abilities${lang}.js`),
    import(`./resources/Items/Items${lang}.js`),
    import(`./resources/Moves/Moves${lang}.js`),
    import(`./resources/Pokes/Pokes${lang}.js`),
    import(`./resources/Natures/Natures${lang}.js`)
  ]);

  return {
    types: types.default,
    abilities: abilities.default,
    items: items.default,
    moves: moves.default,
    pokes: pokes.default,
    natures: natures.default
  };
}

/**
 * CARGAR FUENTES BASE
 */
function loadBaseFonts(doc) {
  doc.addFileToVFS('text1.ttf', text1);
  doc.addFont('text1.ttf', 'text1', 'normal');

  doc.addFileToVFS('text2.ttf', text2);
  doc.addFont('text2.ttf', 'text2', 'normal');

  doc.addFileToVFS('text3.ttf', text3);
  doc.addFont('text3.ttf', 'text3', 'normal');
}